/**
 * Market-hours helpers. One dependency, the generated calendar data
 * (`lib/nyseCalendar.gen.ts`, which imports nothing), so client and server can
 * both import this — useful for adaptive cache TTLs (server) and adaptive poll
 * intervals (client) that pause/relax outside US Regular Trading Hours.
 *
 * RTH = Mon–Fri, 09:30–16:00 America/New_York, excluding NYSE holidays
 * and respecting early-close days (close at 13:00 ET).
 */

import {
  NYSE_CALENDAR_FROM,
  NYSE_CALENDAR_TO,
  NYSE_EARLY_CLOSE_DAYS,
  NYSE_FULL_CLOSE_DAYS,
} from "@/lib/nyseCalendar.gen";

/**
 * The NYSE calendar: full closes and early closes, New York dates
 * (YYYY-MM-DD). Generated from Alpaca's `GET /v2/calendar` by
 * `npm run nyse:calendar` into `lib/nyseCalendar.gen.ts`, checked in, and
 * read here synchronously: nothing is asked at run time (README "The NYSE
 * calendar is generated from Alpaca's"). Until 2026-10-15 these two sets were
 * typed by hand for 2024-2028; `npm run nysecal:check` holds the generated
 * table to that one, day for day, over those years.
 *
 * `npm run schema:check` fails once the last dated holiday is under 12 months
 * away, and the nightly batch asks Alpaca for the coming year and says, loudly,
 * any day that differs from the checked-in table (`npm run nyse:calendar --
 * --drift`), so a closure announced like 2025-01-09 is seen the night it is
 * published, not at the next deploy.
 *
 * The way back: the span read below. `CALENDAR_READ_FROM = "2024-01-01"` and
 * `CALENDAR_READ_TO = "2028-12-31"` read exactly the hand-typed table of
 * before 2026-10-15 (the check proves it), so a weekday before 2024 or in 2029
 * is a session again, as it was then.
 */
const CALENDAR_READ_FROM: string = NYSE_CALENDAR_FROM;
const CALENDAR_READ_TO: string = NYSE_CALENDAR_TO;
const inRead = (d: string) => d >= CALENDAR_READ_FROM && d <= CALENDAR_READ_TO;

/** NYSE full-close days. On these dates the exchange does not open and
 *  `isWithinRTH` returns false all day. */
const NYSE_FULL_CLOSE: ReadonlySet<string> = new Set(NYSE_FULL_CLOSE_DAYS.filter(inRead));

/** NYSE early-close days, with the close as minutes after New York midnight
 *  (13:00 on every one Alpaca lists, from 1993: 780). */
const NYSE_EARLY_CLOSE: ReadonlyMap<string, number> = new Map(
  NYSE_EARLY_CLOSE_DAYS.filter(([d]) => inRead(d)).map(([d, hm]) => [d, Number(hm.slice(0, 2)) * 60 + Number(hm.slice(3, 5))]),
);

const REGULAR_CLOSE_MIN = 16 * 60;

/** The regular session's close on a New York date, in minutes after midnight:
 *  16:00, or the early close the calendar lists. */
function closeMinuteOn(key: string): number {
  return NYSE_EARLY_CLOSE.get(key) ?? REGULAR_CLOSE_MIN;
}

/** The first and last day the clock knows the calendar for (YYYY-MM-DD). A
 *  weekday outside them is read as a session. */
export function nyseCalendarSpan(): { from: string; to: string } {
  return { from: CALENDAR_READ_FROM, to: CALENDAR_READ_TO };
}

/** Every full-close day the clock reads, ascending (for checks). */
export function nyseFullCloseDays(): string[] {
  return [...NYSE_FULL_CLOSE].sort();
}

/** Every early-close day the clock reads with its close (HH:MM), ascending (for checks). */
export function nyseEarlyCloseDays(): Array<[string, string]> {
  return [...NYSE_EARLY_CLOSE.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([d, m]) => [d, `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`]);
}

/** Latest YYYY-MM-DD in the full-close table. `schema:check` requires this
 *  to sit at least 12 months ahead of today so the calendar cannot expire
 *  silently after NYSE's November publish. */
export function lastNyseFullCloseDate(): string {
  let last = "";
  for (const d of NYSE_FULL_CLOSE) if (d > last) last = d;
  return last;
}

/** YYYY-MM-DD in ET for the given ms. Memoized via the nyDateParts call. */
function etDateKey(ms: number): string {
  const p = nyDateParts(ms);
  const mm = String(p.month).padStart(2, "0");
  const dd = String(p.day).padStart(2, "0");
  return `${p.year}-${mm}-${dd}`;
}

/** True if the given moment falls on a full-close NYSE holiday (ET date). */
export function isMarketHoliday(now: number = Date.now()): boolean {
  return NYSE_FULL_CLOSE.has(etDateKey(now));
}

/**
 * True if the **UTC-midnight session anchor** `t` is a NYSE holiday.
 *
 * Use this when checking a stored bar's `t` (which is UTC midnight of the
 * ET session date) — `isMarketHoliday(t)` would incorrectly read that ms
 * as the *previous* day in ET (UTC midnight = 8pm ET prior day).
 *
 *   const t = Date.UTC(2026, 4, 25);   // Memorial Day, UTC midnight
 *   isMarketHoliday(t)                  // false (reads "2026-05-24" in ET)
 *   isMarketHolidayUtc(t)               // true  (reads "2026-05-25" UTC)
 */
export function isMarketHolidayUtc(t: number): boolean {
  const d = new Date(t);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return NYSE_FULL_CLOSE.has(`${y}-${m}-${day}`);
}

/** True if the given moment falls on an early-close day. Does NOT include
 *  the implicit 16:00 → 13:00 close-time change — `isWithinRTH` applies
 *  that automatically. Exposed for UI that wants to label the day. */
export function isEarlyCloseDay(now: number = Date.now()): boolean {
  return NYSE_EARLY_CLOSE.has(etDateKey(now));
}

/** True if the ET clock is inside US equity Regular Trading Hours.
 *  Considers weekends, NYSE full-close holidays, and early-close days. */
export function isWithinRTH(now: number = Date.now()): boolean {
  return marketState(now) === "rth";
}

/**
 * Trichotomy used by status surfaces:
 *
 *   "rth"          — Mon–Fri, inside 09:30–16:00 ET (13:00 on early-close days),
 *                    not a NYSE holiday.
 *   "after-hours"  — Mon–Fri, outside RTH window, not a NYSE holiday.
 *                    Covers pre-market (00:00–09:30 ET) AND after-hours
 *                    (16:00–24:00 ET on a trading day).
 *   "closed"       — Weekends OR NYSE full-close holidays.
 *
 * Early-close days collapse into "after-hours" once the 13:00 close passes —
 * the tape is dark even though the calendar date is still a trading day.
 */
export type MarketState = "rth" | "after-hours" | "closed";

export function marketState(now: number = Date.now()): MarketState {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hour12: false,
  }).formatToParts(new Date(now));
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const wd = m.weekday ?? "";
  if (wd === "Sat" || wd === "Sun") return "closed";
  if (isMarketHoliday(now)) return "closed";
  let h = Number(m.hour);
  if (h === 24) h = 0;
  const mn = Number(m.minute);
  const minutes = h * 60 + mn;
  if (minutes < 9 * 60 + 30) return "after-hours";
  const closeMinute = closeMinuteOn(etDateKey(now));
  if (minutes < closeMinute) return "rth";
  return "after-hours";
}

/**
 * Milliseconds until the next RTH session STARTS, or null if we are already
 * inside one.
 *
 * Exists so a scheduler cannot sleep straight through the opening bell. The
 * scanner picked its cadence at the end of each cycle: a run finishing at
 * 09:06 ET saw a closed market, chose the one-hour idle interval, and woke at
 * ~09:55 — leaving the panel unrefreshed for the first 25 minutes of every
 * session. The decision was correct when made and simply never revisited.
 *
 * Walks forward day by day (capped at a week, which covers any holiday run)
 * and asks `marketState` rather than re-deriving the calendar, so holidays and
 * early closes stay defined in exactly one place.
 */
function etMinutesAt(t: number): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const m = Object.fromEntries(fmt.formatToParts(new Date(t)).map((p) => [p.type, p.value]));
  let h = Number(m.hour);
  if (h === 24) h = 0;
  return h * 60 + Number(m.minute);
}

export function msUntilNextRthStart(now: number = Date.now()): number | null {
  if (marketState(now) === "rth") return null;
  const etMinutes = etMinutesAt;
  const DAY = 86_400_000;
  for (let i = 0; i <= 7; i++) {
    const probe = now + i * DAY;
    // 09:30 ET on the probe day, derived from that day's own ET clock so DST
    // transitions cannot shift it.
    const openAt = probe + (9 * 60 + 30 - etMinutes(probe)) * 60_000;
    if (openAt <= now) continue;
    if (marketState(openAt) === "rth") return openAt - now;
  }
  return null;
}

/**
 * Milliseconds since TODAY's RTH session opened, or null when we are not
 * inside one. The mirror of `msUntilNextRthStart`, derived from the probe
 * day's own ET clock so a DST transition cannot shift it.
 *
 * It exists for freshness judgements, which need to know not just "how old is
 * this payload" but "how long has anything had a chance to refresh it". A
 * worker that idles outside RTH has written nothing at all at 09:30:01, so a
 * staleness rule reading elapsed age alone raises a false alarm at every
 * single open — and a warning that is wrong every morning is one nobody reads
 * by the end of the week. See `lib/scanner/snapshotFreshness.ts`.
 */
export function msSinceRthOpen(now: number = Date.now()): number | null {
  if (marketState(now) !== "rth") return null;
  const openAt = now + (9 * 60 + 30 - etMinutesAt(now)) * 60_000;
  return Math.max(0, now - openAt);
}

/**
 * True once TODAY's regular session has CLOSED on a trading day — i.e. it is
 * a weekday, not a NYSE full-close holiday, and the ET clock is at/after the
 * close (16:00, or 13:00 on an early-close day).
 *
 * This is narrower than `marketState() !== "rth"`: it distinguishes
 * "after today's close" from the other dark-tape states.
 *   - pre-market (before 09:30)               → false (today hasn't traded)
 *   - weekend / NYSE full-close holiday        → false (no session today)
 *   - inside RTH                               → false (session still open)
 *   - post-close → overnight on a trading day  → true
 *
 * The breadth pipeline uses this to decide whether today's bar is a
 * COMPLETED session (include it in the close arrays, rolling the windows to
 * "ending today" at 4pm) or still forming (drop it). It stays true through
 * the rest of the ET calendar day; after midnight the date itself rolls and
 * today's bar is naturally the most recent completed session anyway.
 */
export function hasTodaySessionClosed(now: number = Date.now()): boolean {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hour12: false,
  }).formatToParts(new Date(now));
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const wd = m.weekday ?? "";
  if (wd === "Sat" || wd === "Sun") return false;
  if (isMarketHoliday(now)) return false;
  let h = Number(m.hour);
  if (h === 24) h = 0;
  const minutes = h * 60 + Number(m.minute);
  const closeMinute = closeMinuteOn(etDateKey(now));
  return minutes >= closeMinute;
}

/**
 * True once TODAY's regular session has OPENED on a trading day — i.e. it is
 * a weekday, not a NYSE full-close holiday, and the ET clock is at/after
 * 09:30 (and the session is either still open or already closed).
 *
 * Narrower than `marketState() !== "closed"`: pre-market on a trading day
 * is `"after-hours"` but the cash session has not printed yet. Daily chart
 * bars must not appear until this is true — otherwise a premarket snapshot
 * is drawn as today's candle (Seasonality / Advanced 1d before the bell).
 *
 *   - pre-market (before 09:30)               → false
 *   - inside RTH                               → true
 *   - post-close → overnight on a trading day  → true
 *   - weekend / NYSE full-close holiday        → false
 */
export function regularSessionOpenedToday(now: number = Date.now()): boolean {
  return marketState(now) === "rth" || hasTodaySessionClosed(now);
}

/**
 * Instant of TODAY's 09:30 ET open, or null on a weekend / NYSE holiday.
 * Valid before the bell (the open is still in the future) and after the
 * close (it is this morning's open, not tomorrow's).
 */
export function todayRegularOpenMs(now: number = Date.now()): number | null {
  if (marketState(now) === "closed") return null;
  return now + (9 * 60 + 30 - etMinutesAt(now)) * 60_000;
}

/**
 * Finer-grained session than `marketState` — splits "after-hours" into
 * pre-market and post-market, and treats the overnight gap as closed.
 * Drives the per-pane Live / Pre-Market / Post-Market / Market Closed
 * badge + status bars.
 *
 * **Asset-class aware.** Pre/post/closed windows differ by what's being
 * shown — equities have a 09:30–16:00 RTH with pre/post wings, FX runs
 * 24×5, futures/commodities run ~Globex, crypto is 24/7. Pass the asset
 * class so the badge is correct for the surface. Defaults to `us-equity`.
 *
 * Non-equity classes never return "pre-market"/"post-market" (those are an
 * equities concept) — they're either "live" or "closed".
 *
 * The futures/FX/commodity calendars are best-effort weekly schedules in ET
 * (no per-product nuance or futures holiday calendar yet) — enough to label
 * the badge correctly the vast majority of the time.
 */
export type MarketSession = "pre-market" | "live" | "post-market" | "closed";

/**
 * Ext % is a pre/post-market column. During RTH the regular % columns already
 * own the move, and overnight (closed) there is no tape — so the column and
 * the Ext chip hide in those two states and only appear in pre / post.
 */
export function isExtendedHoursSession(session: MarketSession): boolean {
  return session === "pre-market" || session === "post-market";
}

export type SessionAssetClass =
  | "us-equity"
  | "futures"
  | "commodity"
  | "fx"
  | "crypto";

/** ET weekday short-name + minutes-since-midnight for a moment. */
function etWeekdayMinutes(now: number): { wd: string; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hour12: false,
  }).formatToParts(new Date(now));
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  let h = Number(m.hour);
  if (h === 24) h = 0;
  return { wd: m.weekday ?? "", minutes: h * 60 + Number(m.minute) };
}

export function marketSession(
  now: number = Date.now(),
  asset: SessionAssetClass = "us-equity",
): MarketSession {
  // Crypto: always open.
  if (asset === "crypto") return "live";

  const { wd, minutes } = etWeekdayMinutes(now);
  const isSat = wd === "Sat";
  const isSun = wd === "Sun";
  const isFri = wd === "Fri";

  if (asset === "fx") {
    // 24×5: opens Sun 17:00 ET, closes Fri 17:00 ET. No pre/post.
    const OPEN = 17 * 60;
    if (isSat) return "closed";
    if (isSun) return minutes >= OPEN ? "live" : "closed";
    if (isFri) return minutes < OPEN ? "live" : "closed";
    return "live"; // Mon–Thu, around the clock
  }

  if (asset === "futures" || asset === "commodity") {
    // ~CME Globex: Sun 18:00 ET → Fri 17:00 ET, with a daily maintenance
    // halt 17:00–18:00 ET. Best-effort; no per-product hours. No pre/post.
    const OPEN = 18 * 60; // 18:00 ET
    const CLOSE = 17 * 60; // 17:00 ET
    if (isSat) return "closed";
    if (isSun) return minutes >= OPEN ? "live" : "closed";
    if (isFri) return minutes < CLOSE ? "live" : "closed";
    // Mon–Thu: live except the 17:00–18:00 maintenance break.
    return minutes >= CLOSE && minutes < OPEN ? "closed" : "live";
  }

  // us-equity (default): pre / RTH / post, plus NYSE holidays + early close.
  if (isSat || isSun) return "closed";
  if (isMarketHoliday(now)) return "closed";
  const PRE_OPEN = 4 * 60; // 04:00 ET — pre-market start
  const RTH_OPEN = 9 * 60 + 30; // 09:30 ET
  const RTH_CLOSE = closeMinuteOn(etDateKey(now));
  const POST_CLOSE = 20 * 60; // 20:00 ET — extended-hours end
  if (minutes < PRE_OPEN) return "closed";
  if (minutes < RTH_OPEN) return "pre-market";
  if (minutes < RTH_CLOSE) return "live";
  if (minutes < POST_CLOSE) return "post-market";
  return "closed";
}

/** Ms until the current equity session segment ends (pre → RTH, RTH → post, post → closed). */
export function msUntilSessionEnd(
  now: number = Date.now(),
  asset: SessionAssetClass = "us-equity",
): number | null {
  if (asset !== "us-equity") {
    const boundary = msUntilNextRthBoundary(now);
    return boundary > 0 ? boundary : null;
  }
  const session = marketSession(now, asset);
  const p = nyDateParts(now);
  if (session === "pre-market") {
    const ms = msTo(now, p.year, p.month, p.day, 9, 30);
    return ms > 0 ? ms : null;
  }
  if (session === "live") {
    const close = closeMinuteOn(etDateKey(now));
    const ms = msTo(now, p.year, p.month, p.day, Math.floor(close / 60), close % 60);
    return ms > 0 ? ms : null;
  }
  if (session === "post-market") {
    const ms = msTo(now, p.year, p.month, p.day, 20, 0);
    return ms > 0 ? ms : null;
  }
  return msUntilNextRthStart(now);
}

export function formatDurationLong(ms: number): string {
  if (!(ms > 0) || !Number.isFinite(ms)) return "";
  const totalMin = Math.round(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return m <= 1 ? "1 minute" : `${m} minutes`;
  if (m === 0) return h === 1 ? "1 hour" : `${h} hours`;
  const hs = h === 1 ? "1 hour" : `${h} hours`;
  const msLabel = m === 1 ? "1 minute" : `${m} minutes`;
  return `${hs} and ${msLabel}`;
}

/**
 * Ms since the market's most recent close — a property of the MARKET, not
 * of any one pane's data, so every surface for the same asset class agrees
 * (fixes panes drifting apart because their `asOf` anchors differed). Drives
 * the "Market Closed · Nh ago" status when closed.
 *
 *   us-equity        — most recent trading-day close (16:00 ET, 13:00 on
 *                      early-close days), skipping weekends + NYSE holidays
 *   fx               — most recent Fri 17:00 ET (the weekly close)
 *   futures/commodity — most recent weekday 17:00 ET (daily close / break)
 *   crypto           — null (never closes)
 *
 * Returns null if no close is found in the trailing ~10 days (shouldn't
 * happen) or for crypto.
 */
export function msSinceLastClose(
  now: number = Date.now(),
  asset: SessionAssetClass = "us-equity",
): number | null {
  if (asset === "crypto") return null;
  for (let i = 0; i < 10; i++) {
    const t = now - i * 24 * 60 * 60 * 1000;
    const p = nyDateParts(t);
    const key = etDateKey(t);
    const weekend = p.weekday === "Sat" || p.weekday === "Sun";
    let closeMinute: number;
    if (asset === "us-equity") {
      if (weekend || NYSE_FULL_CLOSE.has(key)) continue;
      closeMinute = closeMinuteOn(key);
    } else if (asset === "fx") {
      if (p.weekday !== "Fri") continue;
      closeMinute = 17 * 60;
    } else {
      // futures / commodity
      if (weekend) continue;
      closeMinute = 17 * 60;
    }
    const delta = msTo(now, p.year, p.month, p.day, Math.floor(closeMinute / 60), closeMinute % 60); // close − now
    if (delta <= 0) return -delta; // this close already happened
  }
  return null;
}

/**
 * End of the most recent US-equity EXTENDED session (20:00 ET of the last
 * trading day) at or before `now`. Early-close days still run post-market to
 * 20:00, so the extended end is 20:00 regardless of the 13:00 RTH close.
 *
 * This is the freeze point for intraday HISTORY: between one session's 20:00
 * and the next 04:00 pre-market open, no new equity bar can exist, so a bars
 * request whose `to` is clamped here is byte-identical all night — which is
 * what lets both bar caches actually hit overnight instead of rotating with
 * the wall clock.
 */
export function lastExtendedSessionEndMs(now: number = Date.now()): number {
  for (let i = 0; i < 10; i++) {
    const t = now - i * 24 * 60 * 60 * 1000;
    const p = nyDateParts(t);
    const key = etDateKey(t);
    if (p.weekday === "Sat" || p.weekday === "Sun") continue;
    if (NYSE_FULL_CLOSE.has(key)) continue;
    const delta = msTo(now, p.year, p.month, p.day, 20, 0); // 20:00 ET − now
    if (delta <= 0) return now + delta;
  }
  return now;
}

/**
 * Pick a TTL/interval based on whether the market is currently open.
 *
 *   adaptiveSeconds({ rth: 60, closed: 1800 })
 *
 * 60 s during RTH (matches scanner-worker's 5-min cycle while still letting
 * panes feel "live"), 30 min when closed (the data literally cannot change
 * faster than the next EOD update).
 */
export function adaptiveSeconds(opts: {
  rth: number;
  closed: number;
  now?: number;
}): number {
  return isWithinRTH(opts.now) ? opts.rth : opts.closed;
}

/**
 * Ms until the next RTH transition (open → close, or close → open). Useful
 * for client polling that wants to wake up at the next inflection point.
 * Falls back to 1h if the calculation gets weird.
 *
 * Skips weekends + NYSE holidays when searching forward for the next open.
 */
export function msUntilNextRthBoundary(now: number = Date.now()): number {
  const inRth = isWithinRTH(now);
  const today = nyDateParts(now);
  if (inRth) {
    // Next boundary = today's close (16:00 normally, 13:00 on early-close days).
    const close = closeMinuteOn(etDateKey(now));
    return msTo(now, today.year, today.month, today.day, Math.floor(close / 60), close % 60);
  }
  // Next boundary = next trading day's open (09:30 ET).
  for (let i = 0; i < 14; i++) {
    const t = now + i * 24 * 60 * 60 * 1000;
    const p = nyDateParts(t);
    if (p.weekday === "Sat" || p.weekday === "Sun") continue;
    if (NYSE_FULL_CLOSE.has(etDateKey(t))) continue;
    const open = msTo(now, p.year, p.month, p.day, 9, 30);
    if (open > 0) return open;
  }
  return 60 * 60 * 1000;
}

function nyDateParts(ms: number) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(ms));
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return {
    year: Number(m.year),
    month: Number(m.month),
    day: Number(m.day),
    weekday: m.weekday ?? "",
    hour: Number(m.hour === "24" ? "0" : m.hour),
    minute: Number(m.minute),
  };
}

function msTo(
  now: number,
  year: number,
  month: number,
  day: number,
  targetHourEt: number,
  targetMinEt: number,
): number {
  // Build a UTC ms for the target ET wall-clock by iterating: the offset
  // changes across DST, so a single naive conversion can be off by an hour.
  // We pick a candidate, read back its ET hour, and adjust if needed.
  let candidate = Date.UTC(year, month - 1, day, targetHourEt + 5, targetMinEt);
  for (let i = 0; i < 3; i++) {
    const p = nyDateParts(candidate);
    const delta = (targetHourEt - p.hour) * 60 + (targetMinEt - p.minute);
    if (delta === 0) break;
    candidate += delta * 60 * 1000;
  }
  return candidate - now;
}
